import * as Sentry from '@sentry/browser';

import { subscriptionStatus } from '../Model/Subscription';

/**
 * Helpers for the user-doc sub-property maps (`coachData.{coachId}` and
 * `contracts.{contractId}`).
 */

// Ranking used to pick the "best" subscription status across a coach's contracts.
const SUBSCRIPTION_STATUS_RANK = [
  'ACTIVE',
  'TRIALING',
  'PAST_DUE',
  'UNPAID',
  'CANCELED',
  'INCOMPLETE',
  'INCOMPLETE_EXPIRED',
];

const statusRank = (status) => {
  const rank = SUBSCRIPTION_STATUS_RANK.indexOf(status);
  return rank === -1 ? SUBSCRIPTION_STATUS_RANK.length : rank;
};

const timestampMillis = (value) => (typeof value?.toMillis === 'function' ? value.toMillis() : 0);

/**
 * All of a coach's contract entries, each carrying its own `contractId`. The
 * primitive the rest of this module is built from; `getContractView` ranks these.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @param {string} coachId - The coach whose contracts to consider.
 * @return {Array<object>} The coach's entries; empty when there are none.
 */
const getContractEntries = (contractsMap, coachId) => Object.entries(contractsMap || {})
  .filter(([, contract]) => contract?.coachId === coachId)
  .map(([contractId, contract]) => ({ contractId, ...contract }));

// Statuses under which a contract is still live; past due is a grace period. Not the pod list, on purpose.
const LIVE_CONTRACT_STATUSES = [subscriptionStatus.ACTIVE, subscriptionStatus.TRIALING, subscriptionStatus.PAST_DUE];

/**
 * Whether an entry is live by subscription status; an entry without one falls back to `isActive`.
 *
 * @param {object} contract - A contract entry.
 * @return {boolean} True when the status is ACTIVE, TRIALING or PAST_DUE.
 */
const isLiveContractEntry = (contract) => (
  contract?.subscriptionStatus
    ? LIVE_CONTRACT_STATUSES.includes(contract.subscriptionStatus.toUpperCase())
    : contract?.isActive === true
);

const getFirstServiceStartAt = (contractsMap) => {
  const starts = Object.values(contractsMap || {})
    .filter((contract) => contract?.coachId && contract?.serviceStartAt)
    .map((contract) => timestampMillis(contract.serviceStartAt))
    .filter(Boolean);

  return starts.length ? new Date(Math.min(...starts)) : undefined;
};

/**
 * Picks the single contract that represents a coach relationship in the web
 * UI. A user may hold several contracts with the same coach; the "best" one
 * wins (active first, then best status, then latest started episode) and all
 * view fields are read from it.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @param {string} coachId - The coach whose contracts to consider.
 * @return {object} The best contract (plus `contracts`, all of the coach's
 * entries); empty when the coach has none.
 */
const getContractView = (contractsMap, coachId) => {
  const contracts = getContractEntries(contractsMap, coachId);

  if (!contracts.length) {
    return {};
  }

  const best = [...contracts].sort((a, b) => (
    (!!b.isActive - !!a.isActive)
    || (statusRank(a.subscriptionStatus) - statusRank(b.subscriptionStatus))
    || (timestampMillis(b.serviceStartAt) - timestampMillis(a.serviceStartAt))
  ))[0];

  return {
    ...best,
    contracts,
  };
};

/**
 * The coach's contract that was in force on a date — the episode a dated artefact
 * belongs to. Not `getContractView`, which ranks by "best": a re-onboarded client's
 * newest contract starts after an older check-in, so the best entry would date that
 * check-in against an episode it predates.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @param {string} coachId - The coach whose contracts to consider.
 * @param {Date} at - The moment to date against.
 * @return {object|undefined} The latest entry started by `at`, with its `contractId`.
 */
const getContractEntryAtDate = (contractsMap, coachId, at) => {
  if (!at) {
    return undefined;
  }

  return getContractEntries(contractsMap, coachId)
    .filter((contract) => {
      const startedAt = timestampMillis(contract.serviceStartAt);
      return startedAt > 0 && startedAt <= at.getTime();
    })
    .sort((a, b) => timestampMillis(b.serviceStartAt) - timestampMillis(a.serviceStartAt))[0];
};

/**
 * One entry by id, whatever coach it names. Entries seeded at checkout carry no `coachId`
 * until activation adds it, so a coach-scoped lookup cannot see a client mid-payment.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @param {string} contractId - The contract to look up.
 * @return {object|undefined} `{ contractId, ...entry }` when present.
 */
const getContractEntryById = (contractsMap, contractId) => {
  const entry = contractsMap?.[contractId];
  return entry ? { contractId, ...entry } : undefined;
};

/**
 * The doc's only contract, whatever coach it names — the fallback when the coach
 * cannot be resolved. One entry is unambiguous; several without a coach are not, so
 * those return undefined rather than a guess.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @return {object|undefined} The entry, with its `contractId`.
 */
const getSoleContractEntry = (contractsMap) => {
  const [contractId, ...rest] = Object.keys(contractsMap || {});
  return rest.length ? undefined : getContractEntryById(contractsMap, contractId);
};

/**
 * The coach's live contract entries. Anything asking about the client as a
 * whole has to read all of these rather than the single best-ranked entry,
 * because a client can hold one per product.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @param {string} coachId - The coach whose contracts to consider.
 * @return {Array<object>} The live entries.
 */
const getActiveContractEntries = (contractsMap, coachId) => (
  getContractEntries(contractsMap, coachId).filter((contract) => contract.isActive)
);

/**
 * Whether EVERY live product of this coach is on a break. A client is only really
 * paused when nothing of theirs is running, so one paused product must not silence
 * a product that is still being billed. False when no product is live: there is
 * nothing to be on a break from.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @param {string} coachId - The coach whose contracts to consider.
 * @return {boolean} True when every live product is on a break.
 */
const isOnBreakForAllProducts = (contractsMap, coachId) => {
  const entries = getActiveContractEntries(contractsMap, coachId);
  return entries.length > 0
    && entries.every((contract) => timestampMillis(contract.breakEndDate) > Date.now());
};

/**
 * Whether NO live product of this coach has started yet. Buying a second product
 * that starts next month must not lock the client out of the one they already use.
 * False when no product is live.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @param {string} coachId - The coach whose contracts to consider.
 * @return {boolean} True when no live product has started.
 */
const isFutureStartForAllProducts = (contractsMap, coachId) => {
  const entries = getActiveContractEntries(contractsMap, coachId);
  return entries.length > 0
    && entries.every((contract) => timestampMillis(contract.serviceStartAt) > Date.now());
};

/**
 * Distinct product ids the client holds live with this coach. Distinct matters:
 * amendments and re-onboarding mint a new contract id for the same product, so
 * a single product routinely has several entries.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @param {string} coachId - The coach whose contracts to consider.
 * @return {Array<string>} The live product ids.
 */
const getActiveProductIds = (contractsMap, coachId) => [
  ...new Set(
    getActiveContractEntries(contractsMap, coachId)
      .map((contract) => contract.productId)
      .filter(Boolean),
  ),
];

/**
 * Whether the client holds more than one live product with this coach — the
 * gate for showing a product selector at all. Strictly live entries only: a
 * client whose products are all cancelled must not be offered a choice.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @param {string} coachId - The coach whose contracts to consider.
 * @return {boolean} True when more than one live product is held.
 */
const hasMultipleActiveProducts = (contractsMap, coachId) => (
  getActiveProductIds(contractsMap, coachId).length > 1
);

/**
 * Whether the user already holds a live contract with this coach for this
 * product. A client may hold several subscriptions with one coach, one per
 * product, so "already subscribed" is only a blocker for the SAME product —
 * automated onboarding clones from a single global base programme, so a second
 * copy of the same product delivers nothing new.
 *
 * @param {object} contractsMap - The user doc `contracts` map.
 * @param {string} coachId - The coach being bought from.
 * @param {string} productId - The product being bought.
 * @return {object|undefined} `{ contractId, ...entry }` when one is live.
 */
const findLiveContractForProduct = (contractsMap, coachId, productId) => {
  if (!coachId || !productId) {
    return undefined;
  }
  const match = Object.entries(contractsMap || {}).find(([, contract]) => (
    contract?.coachId === coachId
    && contract?.productId === productId
    && !!contract?.isActive
  ));
  return match ? { contractId: match[0], ...match[1] } : undefined;
};

/**
 * Derives the coach a user doc should be viewed as when no explicit coach
 * context was provided. Mirrors the server's resolvePrimaryCoachId
 * (functions-22/src/db/userDocV2.ts) step for step so web and server never
 * disagree on the same doc: single entry → it; single ACTIVE entry → it;
 * several actives → root `assignedCoach` tie-break (not a migrated field —
 * the coach-assignment flow keeps it pointing at the current coach). Only a
 * genuinely ambiguous doc returns undefined instead of guessing — a guess
 * beyond these rules could surface one coach's private data (notes,
 * insights) on another coach's screen; those contexts must pass
 * `viewAsCoachId` explicitly. `assignedCoaches` is deliberately NOT used to
 * widen the candidate set: it is an append-only history, so it goes stale
 * the moment a client switches coach.
 *
 * @param {object} data - The user doc data.
 * @return {string|undefined} The resolved coach id.
 */
const resolvePrimaryCoachId = (data) => {
  const coachData = data?.coachData || {};
  const coachIds = Object.keys(coachData);
  if (!coachIds.length) {
    // Unmigrated doc (no entries yet): the append-only history is safe to
    // trust only when it names exactly one coach.
    const historic = data?.assignedCoaches || [];
    return historic.length === 1 ? historic[0] : undefined;
  }
  if (coachIds.length === 1) {
    return coachIds[0];
  }
  const activeCoachIds = coachIds.filter((id) => !!coachData[id]?.flags?.isActive);
  if (activeCoachIds.length === 1) {
    return activeCoachIds[0];
  }
  const candidateIds = activeCoachIds.length ? activeCoachIds : coachIds;
  if (data?.assignedCoach && candidateIds.includes(data.assignedCoach)) {
    return data.assignedCoach;
  }
  return undefined;
};

const reportedOnce = new Set();

/**
 * Reports a backfill gap to Sentry once per key per session, so a screen that
 * re-renders does not repeat itself.
 *
 * @param {string} key - Dedupe key.
 * @param {string} message - Sentry message.
 * @param {object} extra - Sentry extra context.
 */
const reportOnce = (key, message, extra) => {
  if (reportedOnce.has(key)) {
    return;
  }
  reportedOnce.add(key);
  Sentry.captureMessage(message, { level: 'warning', extra });
};

/**
 * Reports a loaded user doc that has no `coachData.{coachId}` entry for the
 * coach the UI is explicitly scoped to — a backfill gap that would otherwise
 * silently render empty client state.
 *
 * @param {string} userId - The user doc id.
 * @param {string} coachId - The explicitly scoped coach id (`viewAsCoachId`).
 */
const reportMissingCoachData = (userId, coachId) => reportOnce(
  `coachData:${userId}:${coachId}`,
  'v2 coachData entry missing for explicitly scoped coach',
  { userId, coachId },
);

export {
  findLiveContractForProduct,
  getActiveProductIds,
  getContractEntries,
  getContractEntryAtDate,
  getContractEntryById,
  getContractView,
  getFirstServiceStartAt,
  getSoleContractEntry,
  hasMultipleActiveProducts,
  isFutureStartForAllProducts,
  isLiveContractEntry,
  isOnBreakForAllProducts,
  reportMissingCoachData,
  reportOnce,
  resolvePrimaryCoachId,
};
